import { useCallback, useContext } from 'react';

import { BreakpointContext } from './BreakpointProvider';
import type { Breakpoint, ResponsiveProp } from './breakpoints';

/**
 * Access breakpoint context for managing responsive properties.
 *
 * The returned function takes a responsive property as its only argument and
 * returns the value of the largest matching breakpoint, based on the current
 * viewport dimensions.
 */
export function useResponsiveProp() {
  const matchedBreakpoints = useContext(BreakpointContext);

  return useCallback(
    // Prefer declaration over lambda in this case. Retain the function name in
    // development for improved debugging.
    function responsiveProp<T>(prop: ResponsiveProp<T>): T | undefined {
      return getResponsiveProp(prop, matchedBreakpoints);
    },
    [matchedBreakpoints]
  );
}

/** @private Exported for internal use. */
export function getResponsiveProp<T>(
  prop: ResponsiveProp<T>,
  matchedBreakpoints: Breakpoint[]
): T | undefined {
  for (let i = 0; i < matchedBreakpoints.length; i++) {
    const value = prop[matchedBreakpoints[i]!];
    if (value != null) {
      return value;
    }
  }

  return undefined;
}
